<template>
  <div>
    <v-card>
      <v-card-text>
        <v-form id="user-form" @submit.prevent="onSubmit" autocomplete="off" name="lastpass-disable-search">
          <v-container>
            <v-row>
              <v-col cols="9">
                <v-text-field
                  :label="$t('email')"
                  v-model="localModel.email"
                  :error-messages="emailErrors"/>
              </v-col>
              <v-col>
                <v-switch
                  v-if="action === 'create'"
                  v-model="localModel.sendWelcome"
                  label="Send Welcome Email"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  :label="$t('first_name')"
                  v-model="profile.firstName"
                  hint="Optional - user can set during first login"/>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  :label="$t('last_name')"
                  v-model="profile.lastName"
                  hint="Optional - user can set during first login"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  :label="$t('phone_number')"
                  v-model="profile.phone"
                  hint="Optional - user can set during first login"/>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  :label="$t('job_title')"
                  v-model="profile.title"
                  hint="Optional - user can set during first login"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  prepend-inner-icon="mdi-lock"
                  :label="$t('password')"
                  v-model="localModel.password"
                  :error-messages="passwordErrors"
                  :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="() => (revealPassword = !revealPassword)"
                  :type="revealPassword ? 'text' : 'password'"/>
                <password v-model="localModel.password" :strength-meter-only="true"/>
                <div class="password-strength pb-2">{{ $t('password_strength') }}</div>
                <v-text-field
                  prepend-inner-icon="mdi-lock"
                  id="user-repeat-password"
                  v-model="localModel.repeatPassword"
                  :error-messages="repeatPasswordErrors"
                  :placeholder="$t('reenter_password')"
                  :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="() => (revealPassword = !revealPassword)"
                  :type="revealPassword ? 'text' : 'password'"/>
              </v-col>
            </v-row>
            <v-row v-if="user.can('view_rootentity')" class="root-only">
              <v-col>
                <h4>{{ $t('admin_only') }}</h4>
                <vf-autocomplete
                  v-model="localModel.roles"
                  :items="roles"
                  item-text="name"
                  item-value="id"
                  :error-messages="roleErrors"
                  :label="$t('assigned_roles')"
                  return-object/>
                <div v-if="!hasRootRoleAssigned">
                  <vf-autocomplete
                    v-model="localModel.customers"
                    :items="allCustomers"
                    item-text="name"
                    item-value="id"
                    :label="$t('customer_assignment')"
                    return-object/>
                </div>
                <div class="form-group row">
                  <label for="resource-restriction-rules" class="col-sm-3 col-form-label text-sm-right">Resource
                    Restriction
                    Rules</label>
                  <div class="col-sm-6">
                    <json-editor
                      ref="resource-restriction-rule-set"
                      :disable_properties="true"
                      :disable_edit_json="true"
                      :disable_array_reorder="true"
                      :schema="resourceRestrictionsSchema"
                      :disable_collapse="true"
                      :startval='defaultResourceRestrictionRuleSet'/>
                  </div>
                  <div class="col-sm-3 field-info">Use the editor to the left to create rules to restrict a user's access
                    to resources. To do so select the properties button and name a resource (e.g. vehicles, user_settings,
                    etc...).<br/><br/> You may then add individual restrictions. All restrictions must be met in order for
                    the user to have access to the resource. To define a group of restrictions, any one of which can be
                    met to satisfy the requirement select "Restriction Group" the desired restrictions.
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <div class="d-flex justify-end mt-3">
            <v-btn
              v-if="action === 'create' && user.can('create_user')"
              type="submit"
              color="primary"
              @click="$gtag.event('create', {event_category: 'User'})"
              :loading="isSaving">
              <v-icon>mdi-plus</v-icon>
              {{ $t('create') }}
            </v-btn>
            <v-btn
              v-else
              type="submit"
              color="primary"
              :loading="isSaving"
              @click="$gtag.event('create', {event_category: 'User'})">
              <v-icon>mdi-content-save</v-icon>
              {{ $t('save_changes') }}
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Password from 'vue-password-strength-meter'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import JsonEditor from '@/components/shared/JsonEditor'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import resourceRestrictionsSchema from './resourceRestrictionsSchema.js'
import { required, email, sameAs, minLength } from 'vuelidate/lib/validators'
import { forEach, cloneDeep, isEmpty, isEqual, differenceWith, filter, get } from 'lodash'
require('@/polyfills/DomTokenListMultipleArguments.js')

const MIN_PASSWORD_LENGTH = 8
const newModel = function (vm) {
  return vm.id == null
}

export default {
  name: 'UserForm.vue',
  components: {
    Password,
    VfAutocomplete,
    JsonEditor
  },
  mixins: [
    CurrentUserMixin,
    ValidationMixin
  ],
  props: {
    model: Object
  },
  validations: {
    localModel: {
      email: {
        required,
        email
      },
      roles: {
        required,
        minLength: minLength(1)
      },
      password: {
        required: function (value, vm) {
          if (newModel(vm)) return value != null
          return true
        },
        minLength: function (value, vm) {
          if (newModel(vm) || !isEmpty(value)) {
            return value != null && value.length >= MIN_PASSWORD_LENGTH
          }
          return true
        },
        mustContainNumber: function (value, vm) {
          if (vm.action === 'create' || !isEmpty(value)) {
            return /\d/.test(value)
          }
          return true
        },
        mustContainLowerCase: function (value, vm) {
          if (vm.action === 'create' || !isEmpty(value)) {
            return /[a-z]/.test(value)
          }
          return true
        },
        mustContainUpperCase: function (value, vm) {
          if (vm.action === 'create' || !isEmpty(value)) {
            return /[A-Z]/.test(value)
          }
          return true
        }
      },
      repeatPassword: {
        sameAsPassword: sameAs('password')
      }
    }
  },
  data () {
    // When creating a new model, set default values for keys we want to ensure are reactive in Vue.
    const defaultObj = {
      roles: [],
      customers: [],
      sendWelcome: !this.model // Only send welcome email if creating a new user
    }
    const localModel = cloneDeep(this.model) || this.$orm('User').createRecord(defaultObj)

    // Load existing profile data if editing an existing user
    const profile = this.model ? (this.model.getSetting('profile') || {}) : {}

    return {
      MIN_PASSWORD_LENGTH: MIN_PASSWORD_LENGTH,
      localModel: localModel,
      profile: profile,
      loading: false,
      isSaving: false,
      roles: [],
      allCustomers: [],
      resourceRestrictionsSchema: resourceRestrictionsSchema,
      revealPassword: false
    }
  },
  created () {
    // Load the roles and customers for root users (all other users are not
    // allowed to assign roles/customers so it's unnecessary)
    if (this.user.can('view_rootentity')) {
      this.$orm('Role').findAll()
        .then(response => {
          this.roles = response.getItems()
        })
        .catch(error => {
          this.handleError(error)
        })

      this.$orm('Customer')
        .findAll({
          fields: 'id,name',
          'per-page': 0
        })
        .then(response => {
          this.allCustomers = response.getItems()
        })
        .catch(error => {
          this.handleError(error)
        })
    }
  },
  /**
   * Get the current action (so we can handle update/create differently)
   * @return {string}
   */
  computed: {
    action () {
      return this.localModel.id == null ? 'create' : 'update'
    },

    /**
     * Indicates if the user has the root role assigned
     *
     * @var boolean
     */
    hasRootRoleAssigned () {
      return filter(this.localModel.roles, { code: 'root' }).length !== 0
    },

    /**
     * Returns an object containing resource restrictions associated with this
     * user. If there are none then an empty object is returned.
     *
     * @var Object
     */
    defaultResourceRestrictionRuleSet () {
      return get(this.model, 'resource_restriction_rule_set.rule_set', {})
    },

    emailErrors () {
      return this.validationErrors([{
        prop: this.$v.localModel.email,
        rule: 'required',
        message: this.$t('required')
      }])
    },

    roleErrors () {
      return this.validationErrors([{
        prop: this.$v.localModel.roles,
        rule: 'required',
        message: this.$t('required')
      }, {
        prop: this.$v.localModel.roles,
        rule: 'minLength',
        message: this.$t('required')
      }])
    },

    passwordErrors () {
      return this.validationErrors([{
        prop: this.$v.localModel.password,
        rule: 'required',
        message: this.$t('required')
      }, {
        prop: this.$v.localModel.password,
        rule: 'minLength',
        message: this.$t('password_min_length', { minLength: this.MIN_PASSWORD_LENGTH })
      }, {
        prop: this.$v.localModel.password,
        rule: 'mustContainNumber',
        message: this.$t('password_must_have_number')
      }, {
        prop: this.$v.localModel.password,
        rule: 'mustContainLowerCase',
        message: this.$t('password_must_have_lower_case')
      }, {
        prop: this.$v.localModel.password,
        rule: 'mustContainUpperCase',
        message: this.$t('password_must_have_upper_case')
      }])
    },

    repeatPasswordErrors () {
      return this.validationErrors([{
        prop: this.$v.localModel.repeatPassword,
        rule: 'required',
        message: this.$t('required')
      }, {
        prop: this.$v.localModel.repeatPassword,
        rule: 'sameAsPassword',
        message: this.$t('password_must_match')
      }])
    }
  },
  methods: {
    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) return false

      this.isSaving = true

      this.$orm('User').createOrUpdate(this.localModel)
        .then((user) => {
          const additionalOperations = []

          const rolesToAssign = this.computeRecordsToAdd(get(this.model, 'roles', []), this.localModel.roles)
          forEach(rolesToAssign, function (role) {
            additionalOperations.push(user.assignRole(role.id))
          })

          const rolesToRemove = this.computeRecordsToRemove(get(this.model, 'roles', []), this.localModel.roles)
          forEach(rolesToRemove, function (role) {
            additionalOperations.push(user.removeRole(role.id))
          })

          if (!this.hasRootRoleAssigned) {
            const customersToAssign = this.computeRecordsToAdd(get(this.model, 'customers', []), this.localModel.customers)
            forEach(customersToAssign, function (customer) {
              additionalOperations.push(user.assignCustomer(customer.id))
            })

            const customersToRemove = this.computeRecordsToRemove(get(this.model, 'customers', []), this.localModel.customers)
            forEach(customersToRemove, function (customer) {
              additionalOperations.push(user.removeCustomer(customer.id))
            })
          }

          if (!isEqual(this.defaultResourceRestrictionRuleSet, this.$refs['resource-restriction-rule-set'].json)) {
            const ruleSet = get(this, 'model.resource_restriction_rule_set') || {
              entity_id: user.id,
              type: 'common\\models\\User'
            }
            ruleSet.rule_set = this.$refs['resource-restriction-rule-set'].json
            // If the rule_set is an empty object (no rules) then delete the rule_set
            if (isEqual(ruleSet.rule_set, {})) {
              additionalOperations.push(ruleSet.destroy())
            } else {
              additionalOperations.push(this.$orm('ResourceRestrictionRuleSet').createOrUpdate(ruleSet))
            }
          }

          // Always save profile data to keep it in sync
          // This allows clearing all fields and ensures department is saved
          additionalOperations.push(user.updateSettings({ profile: this.profile }))

          Promise.all(additionalOperations)
            .then(() => {
              this.$router.push('/users', () => {
                this.$store.dispatch(
                  'globalMessagingModule/showSuccessFlashBanner',
                  `${this.$i18n.tc('user')} ${user.email} ${this.$i18n.t('generic_save_success')}`
                )
              })
            })
            .catch(error => {
              this.handleError(error)
            })
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.isSaving = false
        })
    },

    /**
     * Filters the desired set of records against the records already assigned
     * (by record.id) and returns only those not yet assigned
     *
     * @return array
     */
    computeRecordsToAdd (originalRecords, newRecords) {
      return differenceWith(newRecords, originalRecords, (arrVal, othVal) => {
        return arrVal.id === othVal.id
      })
    },

    /**
     * Filters the desired set of records against the records already assigned
     * (by record.id) and returns only those which are currently assigned but
     * should not be
     *
     * @return array
     */
    computeRecordsToRemove (originalRecords, newRecords) {
      return differenceWith(originalRecords, newRecords, (arrVal, othVal) => {
        return arrVal.id === othVal.id
      })
    }
  }
}
</script>

<style lang="scss">

.Password {
  max-width: unset;
  margin: unset;
}

</style>
